import { useCallback, useContext, useRef } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'

/**
 * Returns a guard function that enforces a sequential booking flow:
 *   1. If no travellers are selected → open the traveller selection menu
 *   2. If no dates are selected → open the calendar
 *   3. Otherwise → proceed with the original action
 *
 * Room count is automatically derived from the number of travellers
 * (n travellers => n rooms), so the guard only checks travellers and dates.
 *
 * @returns A function that returns `true` if the guard was triggered (caller should bail),
 *          or `false` if all prerequisites are met (caller should proceed).
 */
export function useMultiRoomBookingGuard() {
  const selectedTravellerEmployeeIds = useAppSelector(state => state.businessTraveller.selectedTravellerEmployeeIds)
  const selectedTravellerGuestCount = useAppSelector(state => state.businessTraveller.selectedTravellerGuestCount)
  const currentBusinessAccount = useAppSelector(state => state.businessTraveller.currentBusinessAccount)

  const { checkInDate } = useContext(OfferPageStateContext)
  const offerPageDispatch = useOfferPageDispatch()

  // Refs so the guard callback always reads the latest values without stale closures.
  const selectedTravellerEmployeeIdsRef = useRef(selectedTravellerEmployeeIds)
  selectedTravellerEmployeeIdsRef.current = selectedTravellerEmployeeIds

  const selectedTravellerGuestCountRef = useRef(selectedTravellerGuestCount)
  selectedTravellerGuestCountRef.current = selectedTravellerGuestCount

  const checkInDateRef = useRef(checkInDate)
  checkInDateRef.current = checkInDate

  const currentBusinessAccountRef = useRef(currentBusinessAccount)
  currentBusinessAccountRef.current = currentBusinessAccount

  const guardBookingFlow = useCallback(() => {
    // Skip guard while business account is still loading (traveller auto-select hasn't run yet).
    const accountStatus = currentBusinessAccountRef.current?.status
    if (!accountStatus || accountStatus === 'pending') return false

    const hasTravellers = (selectedTravellerEmployeeIdsRef.current?.length ?? 0) + (selectedTravellerGuestCountRef.current ?? 0) > 0
    if (!hasTravellers) {
      offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'multiRoomTravellers' })
      return true
    }

    if (!checkInDateRef.current) {
      offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
      return true
    }

    return false
  }, [offerPageDispatch])

  return guardBookingFlow
}
